import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";

/*
    Fogos de artifício em canvas, sem dependências externas. Renderiza um overlay
    fixo no topo da tela (sobre o header). Enquanto `ativo` for true, lança foguetes
    que sobem e explodem em partículas com gravidade e desvanecimento. Ao desativar,
    para de lançar e some quando as partículas restantes se apagam.
*/
// Paleta padrão: cores vivas e saturadas, com bom contraste tanto sobre o
// branco do modal quanto sobre o cinza escuro do fundo (máscara). Evita branco,
// amarelo claro e tons muito escuros, que somem em um dos dois fundos.
// Cores vibrantes/saturadas.
const CORES_PADRAO = [
	"#ff7a00", // laranja vibrante
	"#ff1744", // vermelho vibrante
	"#8e2de2", // roxo vibrante
	"#2979ff", // azul vibrante
	"#ff4081", // magenta vibrante
];

const FogosArtificio = ({ ativo, altura = 220, cores = CORES_PADRAO }) => {
	const canvasRef = useRef(null);
	const rafRef = useRef(null);
	const particulasRef = useRef([]);
	const ativoRef = useRef(false);
	const ultimoLancamentoRef = useRef(0);

	useEffect(() => {
		ativoRef.current = ativo;
		if (ativo) {
			iniciar();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ativo]);

	const explodir = (x, y) => {
		const cor = cores[Math.floor(Math.random() * cores.length)];
		const total = 60 + Math.floor(Math.random() * 30);
		for (let i = 0; i < total; i++) {
			const angulo = (Math.PI * 2 * i) / total;
			const velocidade = 2 + Math.random() * 3.5;
			particulasRef.current.push({
				x,
				y,
				vx: Math.cos(angulo) * velocidade,
				vy: Math.sin(angulo) * velocidade,
				vida: 1,
				decaimento: 0.012 + Math.random() * 0.012,
				cor,
				raio: 2.5 + Math.random() * 2,
			});
		}
	};

	const tick = () => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		const largura = canvas.width;
		const alturaC = canvas.height;

		ctx.clearRect(0, 0, largura, alturaC);

		// Lança novos foguetes enquanto estiver ativo (a cada ~250ms).
		const agora = Date.now();
		if (ativoRef.current && agora - ultimoLancamentoRef.current > 250) {
			ultimoLancamentoRef.current = agora;
			// Origem das explosões num "anel" ao redor do centro (onde fica o
			// modal): mantém o ponto central dos fogos afastado do modal, então
			// só algumas partículas chegam a passar por cima dele.
			const base = Math.min(largura, alturaC);
			const angulo = Math.random() * Math.PI * 2;
			const raioOrigem = (0.32 + Math.random() * 0.16) * base;
			const x = largura / 2 + Math.cos(angulo) * raioOrigem;
			const y = alturaC / 2 + Math.sin(angulo) * raioOrigem;
			explodir(x, y);
		}

		const particulas = particulasRef.current;
		for (let i = particulas.length - 1; i >= 0; i--) {
			const p = particulas[i];
			p.vx *= 0.99;
			p.vy *= 0.99;
			p.vy += 0.04; // gravidade
			p.x += p.vx;
			p.y += p.vy;
			p.vida -= p.decaimento;
			if (p.vida <= 0) {
				particulas.splice(i, 1);
				continue;
			}
			ctx.globalAlpha = Math.max(0, p.vida);
			// Glow branco atrás de cada partícula, realçando as cores vibrantes.
			ctx.shadowColor = "rgba(255, 255, 255, 0.5)";
			ctx.shadowBlur = 4;
			ctx.fillStyle = p.cor;
			ctx.beginPath();
			ctx.arc(p.x, p.y, p.raio, 0, Math.PI * 2);
			ctx.fill();
		}
		ctx.shadowBlur = 0;
		ctx.globalAlpha = 1;

		if (ativoRef.current || particulas.length > 0) {
			rafRef.current = requestAnimationFrame(tick);
		} else {
			rafRef.current = null;
		}
	};

	const iniciar = () => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		canvas.width = window.innerWidth;
		canvas.height = altura;
		if (!rafRef.current) {
			rafRef.current = requestAnimationFrame(tick);
		}
	};

	useEffect(() => {
		return () => {
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
		};
	}, []);

	// Renderiza no body (portal) para sair do stacking context do header e
	// ficar de fato acima do modal (que também é portado para o body).
	return ReactDOM.createPortal(
		<canvas
			ref={canvasRef}
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				width: "100vw",
				height: altura,
				pointerEvents: "none",
				zIndex: 3000,
			}}
		/>,
		document.body,
	);
};

export default FogosArtificio;
